/**
 * useListFilters — synchronise un set de filtres avec l'URL (`?q=...&tags=1,2`).
 *
 * L'URL est la source de verite : refresh, partage de lien, back/forward
 * navigateur preservent l'etat exact de la liste filtree. La page parente
 * fournit un schema Zod qui decrit la forme des filtres ; le hook parse
 * les `searchParams` au montage et a chaque navigation, valide via Zod
 * (en `safeParse` — si invalide, on retombe sur les defauts), et expose
 * `filters` typees + setters qui font un `replace` de l'URL (pas de pollution
 * d'historique a chaque frappe).
 *
 *   const schema = z.object({
 *     q: z.string().optional(),
 *     tags: z.array(z.number().int()).optional(),
 *     status: z.enum(['draft','published','archived']).optional(),
 *   })
 *   const { filters, setFilter, removeFilter, clear } = useListFilters(schema)
 *
 * Conventions de serialisation :
 *  - `string` : tel quel (vide -> retire la clef).
 *  - `number` : `String(n)` (NaN -> retire).
 *  - `boolean` : `true` / `false`.
 *  - `Date` : ISO yyyy-mm-dd.
 *  - `array<X>` : CSV `"a,b,c"`. Vide -> retire.
 *
 * Le verrou `<FilterChips>` lit `filters` et expose les actives via
 * `removeFilter(key)`. Pas de notion de "diff vs default" a maintenir.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { z } from 'zod'

type AnyZodObject = z.ZodObject<z.ZodRawShape>

function parseValue(raw: string): string | string[] {
  // Si la valeur contient une virgule, on la traite comme un array.
  // Zod coerce ensuite (string -> number, etc.) si le schema l'attend.
  if (raw.includes(',')) return raw.split(',').filter(Boolean)
  return raw
}

function buildSearchParamsFromObject(obj: Record<string, unknown>): URLSearchParams {
  const next = new URLSearchParams()
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      next.set(key, value.map((v) => String(v)).join(','))
      continue
    }
    if (value instanceof Date) {
      const iso = value.toISOString().slice(0, 10)
      next.set(key, iso)
      continue
    }
    if (typeof value === 'boolean') {
      next.set(key, value ? 'true' : 'false')
      continue
    }
    if (typeof value === 'number' && Number.isNaN(value)) continue
    next.set(key, String(value))
  }
  return next
}

export interface ListFiltersApi<T> {
  filters: T
  setFilter: <K extends keyof T>(key: K, value: T[K] | undefined) => void
  setFilters: (next: Partial<T>) => void
  removeFilter: <K extends keyof T>(key: K) => void
  clear: () => void
  /** Toutes les clefs ayant une valeur active (utile pour `<FilterChips>`). */
  activeKeys: (keyof T)[]
}

export function useListFilters<TSchema extends AnyZodObject>(
  schema: TSchema,
): ListFiltersApi<z.infer<TSchema>> {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<z.infer<TSchema>>(() => {
    const raw: Record<string, unknown> = {}
    for (const [key, value] of searchParams.entries()) {
      raw[key] = parseValue(value)
    }
    const parsed = schema.safeParse(raw)
    if (parsed.success) return parsed.data
    // En cas de mismatch URL/schema, on retombe sur un objet vide pour
    // eviter qu'un parametre legacy fasse crasher la page. Les setters
    // ecraseront proprement au prochain change.
    return schema.parse({})
  }, [searchParams, schema])

  const writeAll = useCallback(
    (next: Record<string, unknown>) => {
      const params = buildSearchParamsFromObject(next)
      setSearchParams(params, { replace: true })
    },
    [setSearchParams],
  )

  const setFilter = useCallback(
    <K extends keyof z.infer<TSchema>>(key: K, value: z.infer<TSchema>[K] | undefined) => {
      const next = { ...filters, [key]: value } as Record<string, unknown>
      writeAll(next)
    },
    [filters, writeAll],
  )

  const setFilters = useCallback(
    (partial: Partial<z.infer<TSchema>>) => {
      const next = { ...filters, ...partial } as Record<string, unknown>
      writeAll(next)
    },
    [filters, writeAll],
  )

  const removeFilter = useCallback(
    <K extends keyof z.infer<TSchema>>(key: K) => {
      const next = { ...filters } as Record<string, unknown>
      delete next[key as string]
      writeAll(next)
    },
    [filters, writeAll],
  )

  const clear = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true })
  }, [setSearchParams])

  const activeKeys = useMemo(() => {
    const keys: (keyof z.infer<TSchema>)[] = []
    for (const [k, v] of Object.entries(filters as Record<string, unknown>)) {
      if (v === undefined || v === null || v === '') continue
      if (Array.isArray(v) && v.length === 0) continue
      keys.push(k as keyof z.infer<TSchema>)
    }
    return keys
  }, [filters])

  return { filters, setFilter, setFilters, removeFilter, clear, activeKeys }
}

export default useListFilters
